/**
 * EF-SECURE-ONBOARDING-AND-PASSWORD-RESET-LINKS-V1
 * 
 * Password Reset Page
 * - Validates reset token
 * - Allows password reset without manual email entry
 * - Supports both admin users and crew candidates
 */

import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Loader2,
  Lock,
  Mail,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PasswordResetPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const userType = searchParams.get("type") || "user";
  
  // States
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  
  // Password states
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [resetComplete, setResetComplete] = useState(false);
  
  // Request reset states (when no token)
  const [requestEmail, setRequestEmail] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  
  // Validate token on mount
  useEffect(() => {
    if (token) {
      validateToken();
    } else {
      setLoading(false);
    }
  }, [token]);
  
  const validateToken = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/auth/validate-reset-token?token=${encodeURIComponent(token)}&type=${userType}`
      );
      
      if (res.ok) {
        const data = await res.json();
        setEmail(data.email);
        setTokenValid(true);
      } else {
        const error = await res.json();
        setTokenError(error.detail || "Invalid or expired reset link");
        setTokenValid(false);
      }
    } catch (err) {
      console.error("Token validation error:", err);
      setTokenError("Unable to validate reset link. Please try again.");
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };
  
  // Password validation
  const validatePassword = () => {
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return false;
    }
    setPasswordError("");
    return true;
  };
  
  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password,
          type: userType,
        }),
      });
      
      // EF-FIX-AUTH-ONBOARDING-TENANT-STATE-CONSISTENCY-V1: Single-read pattern
      const data = await res.json();
      
      if (res.ok) {
        setResetComplete(true);
        toast.success("Password reset successfully!");
      } else {
        toast.error(data?.detail || "Failed to reset password");
      }
    } catch (err) {
      console.error("Reset password error:", err);
      toast.error("Unable to reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle request reset
  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!requestEmail) {
      toast.error("Please enter your email address");
      return;
    }
    
    setRequesting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: requestEmail }),
      });
      
      if (res.ok) {
        setRequestSent(true);
        toast.success("If this email exists, a reset link will be sent.");
      } else {
        toast.error("Unable to process request. Please try again.");
      }
    } catch (err) {
      console.error("Request reset error:", err);
      toast.error("Unable to process request. Please try again.");
    } finally {
      setRequesting(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-6 flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Reset complete state
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <CardTitle>Password Reset Complete</CardTitle>
            <CardDescription>
              Your password has been successfully reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="bg-emerald-500/10 border-emerald-500/20">
              <CheckCircle className="h-4 w-4 text-emerald-400" />
              <AlertDescription className="text-emerald-300">
                You can now login with your new password.
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex justify-center">
            {/* EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1: 
                Redirect to login page - crew will then go to /crew after login */}
            <Button onClick={() => navigate("/login")} data-testid="go-to-login-btn">
              Go to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // No token - show request reset form
  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <KeyRound className="h-12 w-12 text-primary mx-auto mb-4" />
            <CardTitle>Reset Password</CardTitle>
            <CardDescription>
              {requestSent 
                ? "Check your email for the reset link" 
                : "Enter your email to receive a password reset link"}
            </CardDescription>
          </CardHeader>
          
          {requestSent ? (
            <CardContent className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/20">
                <Mail className="h-4 w-4 text-blue-400" />
                <AlertDescription className="text-blue-300">
                  If an account exists with this email, you will receive a password reset link shortly.
                </AlertDescription>
              </Alert>
              <p className="text-sm text-muted-foreground text-center">
                Didn't receive the email? Check your spam folder or{" "}
                <button 
                  className="text-primary hover:underline"
                  onClick={() => setRequestSent(false)}
                >
                  try again
                </button>
              </p>
            </CardContent>
          ) : (
            <form onSubmit={handleRequestReset}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={requestEmail}
                      onChange={(e) => setRequestEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="pl-10"
                      required
                      data-testid="reset-email"
                    />
                  </div>
                </div>
                
                <Button type="submit" className="w-full" disabled={requesting} data-testid="request-reset-btn">
                  {requesting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </CardContent>
            </form>
          )}
          
          <CardFooter className="flex justify-center">
            <Button variant="ghost" onClick={() => navigate("/login")}>
              Back to Login
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // Token error state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Invalid Reset Link</CardTitle>
            <CardDescription>
              {tokenError}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                This reset link may have expired or already been used. Please request a new one.
              </AlertDescription>
            </Alert>
          </CardContent>
          <CardFooter className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate("/login")}>
              Go to Login
            </Button>
            <Button onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.delete("token");
              url.searchParams.delete("type");
              navigate(url.pathname);
            }}>
              Request New Link
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  // Valid token - show password reset form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <KeyRound className="h-12 w-12 text-primary mx-auto mb-4" />
          <CardTitle>Set New Password</CardTitle>
          <CardDescription>
            Create a new password for your account
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleResetPassword}>
          <CardContent className="space-y-4">
            {/* Recognized Email */}
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                <div>
                  <p className="text-xs text-emerald-400">Account Identified</p>
                  <p className="text-sm">{email}</p>
                </div>
              </div>
            </div>
            
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="pl-10 pr-10"
                  required
                  minLength={8}
                  data-testid="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Minimum 8 characters</p>
            </div>
            
            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10"
                  required
                  data-testid="confirm-new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {passwordError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}
            
            <Button type="submit" className="w-full" disabled={submitting} data-testid="reset-password-btn">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </CardContent>
        </form>
        
        <CardFooter className="flex justify-center">
          <p className="text-xs text-muted-foreground">
            Remember your password?{" "}
            <a href="/login" className="text-primary hover:underline">
              Login here
            </a>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
